import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAccount } from 'wagmi';
import {
  isMaybeAddress,
  normalizeAddress,
  loadAllocationsData
} from './allocations';
import Nav from '../../components/Nav';
import Footer from '../../components/Footer';
import './claim.scss';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import confetti from 'canvas-confetti';
import BobberEligibility from './bobber/BobberEligibility';

export default function ClaimChecker() {
  const [input, setInput] = useState('');
  const [error, setError] = useState('');
  const [dataError, setDataError] = useState('');
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [dataset, setDataset] = useState(null);
  const [mounted, setMounted] = useState(false);
  const [bobberTrigger, setBobberTrigger] = useState(0);
  const [bobberDone, setBobberDone] = useState(false);

  const { address, isConnected } = useAccount();

  const normalizedConnectedAddress = useMemo(
    () => (address ? normalizeAddress(address) : ''),
    [address]
  );

  const shortenedAddress = useMemo(() => {
    if (!address) return '';
    const str = String(address);
    return `${str.slice(0, 6)}...${str.slice(-4)}`;
  }, [address]);

  useEffect(() => {
    let cancelled = false;
    setIsLoadingData(true);
    setDataError('');

    loadAllocationsData()
      .then((data) => {
        if (cancelled) return;
        setDataset(data);
        setIsLoadingData(false);
      })
      .catch((err) => {
        console.error('Failed to load allocations', err);
        if (cancelled) return;
        setDataError('Unable to load allocation data. Please try again shortly.');
        setIsLoadingData(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const normalized = useMemo(() => normalizeAddress(input), [input]);
  const looksLikeAddress = useMemo(() => isMaybeAddress(normalized), [normalized]);

  const allocation = useMemo(() => {
    if (!dataset || !looksLikeAddress) return null;
    return dataset.allocationsByAddress[normalized] ?? null;
  }, [dataset, normalized, looksLikeAddress]);

  const hasAllocation =
    !!allocation && (allocation.claim ?? 0) + (allocation.presale ?? 0) > 0;

  const canUseConnectedAddress = Boolean(
    isConnected &&
      address &&
      normalizedConnectedAddress &&
      normalizedConnectedAddress !== normalized
  );

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 10);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (isConnected && address) {
      const lower = String(address).toLowerCase();
      setInput((prev) => (prev ? prev : lower));
      setError('');
    }
  }, [isConnected, address]);

  useEffect(() => {
    if (!hasAllocation || !bobberDone) return;

    const fire = () => {
      confetti({ particleCount: 60, spread: 60, origin: { y: 0.2 }, scalar: 0.8 });
      confetti({
        particleCount: 40,
        spread: 80,
        origin: { y: 0.2, x: 0.8 },
        scalar: 0.9
      });
    };
    const t1 = setTimeout(fire, 250);
    const t2 = setTimeout(
      () =>
        confetti({
          particleCount: 80,
          spread: 100,
          origin: { y: 0.2, x: 0.2 },
          scalar: 0.9
        }),
      650
    );

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [hasAllocation, bobberDone, allocation]);

  const onPasteSample = () => {
    const addresses = dataset?.addresses ?? [];
    if (!addresses.length) return;

    const currentIndex = addresses.indexOf(input);
    const nextIndex = (currentIndex + 1) % addresses.length;
    const nextAddress = addresses[nextIndex];

    setInput(nextAddress);
    setError('');
    setBobberDone(false);
    setBobberTrigger((prev) => prev + 1);
  };

  const onPasteFromClipboard = useCallback(async () => {
    if (typeof navigator === 'undefined' || !navigator.clipboard?.readText) {
      setError('Clipboard access is unavailable in this browser.');
      return;
    }
    try {
      const text = await navigator.clipboard.readText();
      if (!text) {
        setError('Clipboard appears empty. Try copying an address first.');
        return;
      }
      setInput(text.trim());
      setError('');
    } catch (err) {
      console.error('Clipboard paste failed', err);
      setError('Unable to read from clipboard. Paste manually instead.');
    }
  }, []);

  const onUseConnectedAddress = useCallback(() => {
    if (!canUseConnectedAddress || !address) return;
    setInput(String(address));
    setError('');
  }, [address, canUseConnectedAddress]);

  const onCheck = () => {
    setError('');
    if (!looksLikeAddress) {
      setError('Please enter a valid address (0x-prefixed, 40 hex chars).');
      return;
    }
    setBobberDone(false);
    setBobberTrigger((prev) => prev + 1);
  };

  const resultsTagline = (() => {
    if (dataError) return 'Allocation lookup is currently unavailable.';
    if (isLoadingData) return 'Fetching the latest allocation snapshot...';

    if (looksLikeAddress) {
      if (!bobberDone) {
        return 'Reeling in your Bobdrop...';
      }
      if (allocation) {
        return "Here's what this wallet is eligible to claim.";
      }
      return 'This wallet is not eligible in the current snapshot.';
    }

    return 'Enter a wallet in the checker to see if it qualifies.';
  })();

  return (
    <div id="claim-page">
      <Nav useSmoothScroll={false} />

      <header className={`claim-hero ${mounted ? 'in' : ''}`}>
        <div className="claim-container hero-grid">
          <div className="hero-copy-block">
            <div className="hero-top-row">
              <span className="hero-kicker-pill">Community Airdrop</span>
              <span className="status-pill status-live">Live</span>
            </div>

            <h1 className="hero-title">
              <strong>$BOB</strong>{' '}
              <span className="presale-outline">Airdrop</span>
            </h1>

            <p className="hero-subtitle">
              Claim portal for true{' '}
              <span className="bob-font no-outline-bob">Boblievers</span>.
            </p>

            <p className="hero-body">
              <span className="hero-accent">Read-only.</span> Paste any wallet
              or connect to see your airdrop and presale allocations from the
              snapshot - no approvals or transactions required.
            </p>

            <div className="hero-bullets">
              <span>No approvals required</span>
              <span>Merkle-proof verified list</span>
              <span>Wallet or manual address</span>
            </div>
          </div>
        </div>
      </header>

      <main className="claim-main">
        <div className="claim-container">
          <section className={`claim-shell ${mounted ? 'in' : ''}`}>
            <div className="claim-shell-header">
              <div className="shell-copy">
                <h2>Check your wallet's Bobdrop.</h2>
                <p>
                  Plug in any EVM address to see its tier, airdrop allocation, and
                  presale allocation from the snapshot.
                </p>
              </div>
              <div className="shell-pills">
                <span className="shell-pill">Read-only</span>
                <span className="shell-pill">No signatures</span>
                <span className="shell-pill">Snapshot-based</span>
              </div>
            </div>

            <div className="claim-shell-grid">
              <div
                id="checker"
                className={`checker-panel card-panel ${mounted ? 'in' : ''}`}
                style={{ '--checker-bg-image': "url('/claim/bob_airdrop.png')" }}
              >
                <div className="panel-header">
                  <div>
                    <h3>Wallet Checker</h3>
                    <p className="tagline">
                      Enter any EVM wallet or use your connected wallet below.
                    </p>
                  </div>
                </div>

                <div className="checker-body">
                  <div className="cta-block">
                    <div className="connect-row">
                      <ConnectButton showBalance={false} label="Connect Wallet" />
                    </div>

                    <div
                      className={
                        'wallet-status ' +
                        (isConnected
                          ? 'wallet-status--connected'
                          : 'wallet-status--disconnected')
                      }
                    >
                      {isConnected
                        ? `Wallet connected: ${shortenedAddress}`
                        : 'Wallet not connected'}
                    </div>
                  </div>

                  <div className="field-group">
                    <label htmlFor="address-input">Wallet address</label>
                    <div className="row">
                      <div className="input-with-actions">
                        <input
                          id="address-input"
                          type="text"
                          placeholder="0x..."
                          value={input}
                          onChange={(e) => setInput(e.target.value)}
                        />
                        <div className="input-actions">
                          <button
                            type="button"
                            className="icon-button"
                            onClick={onPasteFromClipboard}
                            title="Paste address from clipboard"
                            aria-label="Paste address from clipboard"
                          >
                            <svg viewBox="0 0 24 24" aria-hidden="true">
                              <path d="M16 1H8a2 2 0 0 0-2 2v2H4a2 2 0 0 0-2 2v12c0 1.1.9 2 2 2h10l6-6V5a2 2 0 0 0-2-2h-2V3a2 2 0 0 0-2-2zm-6 2h4v2h-4V3zm10 10.5L16.5 17H14v-3.5L20 13.5zM4 19V7h2v2h12V5h2v8h-4a2 2 0 0 0-2 2v4H4z" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            className="icon-button"
                            onClick={onUseConnectedAddress}
                            title={
                              canUseConnectedAddress
                                ? `Use connected wallet (${shortenedAddress})`
                                : 'Connected wallet already in use'
                            }
                            aria-label="Use connected wallet address"
                            disabled={!canUseConnectedAddress}
                          >
                            <svg viewBox="0 0 24 24" aria-hidden="true">
                              <path d="M17 3H5a2 2 0 0 0-2 2v14c0 1.1.9 2 2 2h12a2 2 0 0 0 2-2v-3h2a2 2 0 0 0 2-2v-4a2 2 0 0 0-2-2h-2V5a2 2 0 0 0-2-2zm0 2v14H5V5h12zm4 6v4h-4v-4h4zm-6 5H7v-2h8v2zm0-4H7v-2h8v2z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="row row-buttons">
                      <button
                        type="button"
                        onClick={onCheck}
                        className="btn primary"
                      >
                        Check wallet
                      </button>
                      <button
                        type="button"
                        onClick={onPasteSample}
                        className="btn"
                        disabled={
                          isLoadingData ||
                          !!dataError ||
                          !(dataset?.addresses?.length)
                        }
                      >
                        Sample (Test)
                      </button>
                    </div>

                    {error && (
                      <div className="helper helper-error">{error}</div>
                    )}
                    {dataError && (
                      <div className="helper helper-error">{dataError}</div>
                    )}
                    {!dataError && isLoadingData && (
                      <div className="helper">
                        Loading allocation data...
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div
                className={
                  'results-panel card-panel ' +
                  (hasAllocation && bobberDone ? 'reveal' : '')
                }
              >
                <div className="panel-header">
                  <div>
                    <h3>Allocation Results</h3>
                    <p className="tagline">{resultsTagline}</p>
                  </div>
                </div>

                <div className="results-body-shell">
                  <div className="results-copy-region">
                    {dataError ? (
                      <div className="muted">
                        Allocation lookup currently unavailable.
                      </div>
                    ) : isLoadingData ? (
                      <div className="muted">
                        Give us a sec while we fetch the allocation list.
                      </div>
                    ) : looksLikeAddress ? (
                      !bobberDone ? (
                        <div className="muted">
                          Bob&apos;s watching the bobber... hang tight.
                        </div>
                      ) : hasAllocation ? (
                        <>
                          <div className="eligibility-pill eligible">
                            Eligible Bobdrop
                          </div>
                          <div className="results-grid">
                            <div className="stat larger fade-in-delay-1">
                              <div className="label larger">
                                <span className="bob-font">BOB</span> Tier
                              </div>
                              <div className="value larger pulse">
                                Tier {allocation?.tier ?? '-1'} |{' '}
                                {allocation?.tierName}{' '}
                                <span className="bob-font">BOB</span>
                              </div>
                            </div>

                            <div className="stat fade-in-delay-1">
                              <div className="label">Claim Allocation</div>
                              <div className="value pulse">
                                {allocation.claim ?? 0}{' '}
                                <span className="bob-font">BOB</span>
                              </div>
                            </div>

                            <div className="stat fade-in-delay-2">
                              <div className="label">Presale Allocation</div>
                              <div className="value pulse">
                                {allocation.presale ?? 0}{' '}
                                <span className="bob-font">BOB</span>
                              </div>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="no-allocation-result">
                          <div className="eligibility-pill ineligible">
                            Not eligible
                          </div>
                          <p className="muted">
                            No allocation found for {normalized} in this snapshot.
                          </p>
                        </div>
                      )
                    ) : (
                      <div className="muted">
                        All Bobs on deck! Enter an address in the checker to see
                        your allocation.
                      </div>
                    )}
                  </div>

                  <div
                    className={
                      'bobber-eligibility-wrapper' +
                      (looksLikeAddress && !bobberDone ? ' visible' : '')
                    }
                  >
                    <BobberEligibility
                      triggerKey={bobberTrigger}
                      isEligible={hasAllocation}
                      onDone={() => setBobberDone(true)}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="claim-footnote">
              This tool is view-only. It never asks for approvals, signatures, or
              transactions - it just reads from the snapshot.
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
